import { useState, useEffect, useRef } from 'react'
import { searchShoppers, type ShopperSearchResult } from '../domain/cxAdminApi'

interface UseSearchShoppersResult {
  data: ShopperSearchResult[] | null
  loading: boolean
  error: string | null
}

/**
 * Searches shoppers via the legacy insta_admin drivers search API, exposing the
 * `{ data, loading, error }` shape of the codegen query hooks so components and
 * tests can treat it like any other read.
 */
export const useSearchShoppers = (query: string): UseSearchShoppersResult => {
  const [data, setData] = useState<ShopperSearchResult[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length === 0) {
      setData(null)
      setLoading(false)
      setError(null)
      return
    }

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    let active = true

    setLoading(true)
    setError(null)
    searchShoppers(trimmed)
      .then(results => {
        if (active) setData(results)
      })
      .catch((err: unknown) => {
        if (!active) return
        setData(null)
        setError(err instanceof Error ? err.message : 'Failed to search shoppers')
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
      controller.abort()
    }
  }, [query])

  return { data, loading, error }
}
